import { useEffect, useState } from 'react'
import {
  Handshake,
  Plus,
  Pencil,
  CheckCircle,
  XCircle,
  AlertCircle,
  X,
  Clock,
  DollarSign,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  listSponsorDeals,
  createSponsorDeal,
  updateSponsorDeal,
  listSponsors,
  listAds,
  listFilmsForAdAssignment,
  type SponsorDealItem,
  type SponsorDealStatus,
  type AdvertiserSponsorItem,
  type AdvertiserAdItem,
  type AdvertiserFilmItem,
} from '@/lib/api/advertiser'

const STATUS_LABELS: Record<SponsorDealStatus, string> = {
  pending: 'Pending',
  accepted: 'Accepted',
  rejected: 'Rejected',
  active: 'Active',
  completed: 'Completed',
}

const STATUS_COLORS: Record<SponsorDealStatus, string> = {
  pending: 'text-yellow-400 bg-yellow-900/20 border-yellow-700',
  accepted: 'text-blue-400 bg-blue-900/20 border-blue-700',
  rejected: 'text-red-400 bg-red-900/20 border-red-700',
  active: 'text-green-400 bg-green-900/20 border-green-700',
  completed: 'text-gray-400 bg-gray-900/20 border-gray-700',
}

const STATUSES: SponsorDealStatus[] = ['pending', 'accepted', 'rejected', 'active', 'completed']

export function AdvertiserDeals() {
  const [deals, setDeals] = useState<SponsorDealItem[]>([])
  const [sponsors, setSponsors] = useState<AdvertiserSponsorItem[]>([])
  const [ads, setAds] = useState<AdvertiserAdItem[]>([])
  const [films, setFilms] = useState<AdvertiserFilmItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  // Modal state
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<SponsorDealItem | null>(null)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState<{
    sponsorId: string
    creatorId: string
    filmId: string
    amount: string
    terms: string
    adId: string
    status: SponsorDealStatus
  }>({
    sponsorId: '',
    creatorId: '',
    filmId: '',
    amount: '',
    terms: '',
    adId: '',
    status: 'pending',
  })

  async function reload() {
    try {
      setLoading(true)
      const [dealsRes, sponsorsRes, adsRes, filmsRes] = await Promise.all([
        listSponsorDeals({ limit: 200 }),
        listSponsors({ limit: 200 }),
        listAds({ limit: 200 }),
        listFilmsForAdAssignment({ limit: 200 }),
      ])
      setDeals(dealsRes.items)
      setSponsors(sponsorsRes.items)
      setAds(adsRes.items)
      setFilms(filmsRes.items)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load deals')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    reload()
  }, [])

  function openCreate() {
    setEditing(null)
    setForm({
      sponsorId: '',
      creatorId: '',
      filmId: '',
      amount: '',
      terms: '',
      adId: '',
      status: 'pending',
    })
    setModalOpen(true)
  }

  function openEdit(d: SponsorDealItem) {
    setEditing(d)
    setForm({
      sponsorId: d.sponsorId,
      creatorId: d.creatorId,
      filmId: d.filmId,
      amount: String(d.amount),
      terms: d.terms ?? '',
      adId: d.adId ?? '',
      status: d.status,
    })
    setModalOpen(true)
  }

  async function handleSave() {
    if (!form.sponsorId || !form.filmId || !form.amount) return
    setSaving(true)
    try {
      if (editing) {
        await updateSponsorDeal(editing.dealId, {
          sponsorId: form.sponsorId,
          creatorId: form.creatorId || undefined,
          filmId: form.filmId,
          amount: Number(form.amount),
          terms: form.terms || undefined,
          adId: form.adId || null,
          status: form.status,
        })
        setNotice('Deal updated.')
      } else {
        await createSponsorDeal({
          sponsorId: form.sponsorId,
          creatorId: form.creatorId,
          filmId: form.filmId,
          amount: Number(form.amount),
          terms: form.terms || undefined,
          adId: form.adId || null,
        })
        setNotice('Deal created.')
      }
      setModalOpen(false)
      await reload()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  async function quickStatus(deal: SponsorDealItem, status: SponsorDealStatus) {
    try {
      await updateSponsorDeal(deal.dealId, { status })
      setNotice(`Deal marked as ${STATUS_LABELS[status]}.`)
      await reload()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Update failed')
    }
  }

  const sponsorName = (id: string) =>
    sponsors.find((s) => s.sponsorId === id)?.name ?? id
  const filmTitle = (id: string) =>
    films.find((f) => f.filmId === id)?.title ?? id

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Sponsor deals</h1>
          <p className="mt-1 text-sm text-gray-400">
            Manage the lifecycle of sponsorship agreements between brands and creators.
          </p>
        </div>
        <Button
          onClick={openCreate}
          className="bg-[#D72638] hover:bg-[#D72638]/90 text-white w-fit"
        >
          <Plus className="w-4 h-4 mr-2" />
          New deal
        </Button>
      </div>

      {/* Notices */}
      {notice && (
        <div className="rounded-lg border border-green-700 bg-green-900/20 px-4 py-2 flex items-center gap-2 text-sm text-green-300">
          <CheckCircle className="w-4 h-4 shrink-0" />
          {notice}
          <button onClick={() => setNotice(null)} className="ml-auto text-green-400 hover:text-green-200">
            <X className="w-3 h-3" />
          </button>
        </div>
      )}
      {error && (
        <div className="rounded-lg border border-red-700 bg-red-900/20 px-4 py-2 flex items-center gap-2 text-sm text-red-300">
          <AlertCircle className="w-4 h-4 shrink-0" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-200">
            <X className="w-3 h-3" />
          </button>
        </div>
      )}

      {/* Status summary pills */}
      {!loading && deals.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {STATUSES.map((s) => {
            const count = deals.filter((d) => d.status === s).length
            if (!count) return null
            return (
              <span
                key={s}
                className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border ${STATUS_COLORS[s]}`}
              >
                {STATUS_LABELS[s]}
                <span className="font-semibold">{count}</span>
              </span>
            )
          })}
        </div>
      )}

      {/* Table */}
      {loading ? (
        <p className="text-sm text-gray-500 animate-pulse">Loading deals…</p>
      ) : deals.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-800 p-12 flex flex-col items-center gap-4 text-center">
          <Handshake className="w-10 h-10 text-gray-700" />
          <div>
            <p className="text-sm font-medium text-gray-400">No sponsor deals yet</p>
            <p className="text-xs text-gray-600 mt-1">
              Create a deal to link a sponsor with a creator and film.
            </p>
          </div>
          <Button
            onClick={openCreate}
            variant="outline"
            className="border-gray-700 text-gray-300 hover:text-white hover:border-[#D72638]"
          >
            <Plus className="w-4 h-4 mr-2" />
            New deal
          </Button>
        </div>
      ) : (
        <div className="rounded-xl border border-gray-800 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800 bg-gray-900/50">
                <th className="px-4 py-3 text-left text-xs uppercase tracking-wide text-gray-500 font-medium">
                  Sponsor
                </th>
                <th className="px-4 py-3 text-left text-xs uppercase tracking-wide text-gray-500 font-medium hidden md:table-cell">
                  Film
                </th>
                <th className="px-4 py-3 text-left text-xs uppercase tracking-wide text-gray-500 font-medium hidden lg:table-cell">
                  Amount
                </th>
                <th className="px-4 py-3 text-left text-xs uppercase tracking-wide text-gray-500 font-medium">
                  Status
                </th>
                <th className="px-4 py-3 text-right text-xs uppercase tracking-wide text-gray-500 font-medium">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {deals.map((d) => (
                <tr
                  key={d.dealId}
                  className="border-b border-gray-900 hover:bg-white/[0.02] transition-colors"
                >
                  <td className="px-4 py-3">
                    <p className="font-medium text-white">{sponsorName(d.sponsorId)}</p>
                    {d.creatorId && (
                      <p className="text-xs text-gray-500 truncate max-w-[160px]">
                        Creator: {d.creatorId.slice(0, 8)}…
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <p className="text-gray-300 truncate max-w-[180px]">{filmTitle(d.filmId)}</p>
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell">
                    <span className="inline-flex items-center gap-1 text-xs text-gray-300">
                      <DollarSign className="w-3 h-3 text-gray-500" />
                      {d.amount.toLocaleString()}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${STATUS_COLORS[d.status]}`}
                    >
                      {d.status === 'pending' && <Clock className="w-3 h-3" />}
                      {d.status === 'active' && <CheckCircle className="w-3 h-3" />}
                      {d.status === 'rejected' && <XCircle className="w-3 h-3" />}
                      {STATUS_LABELS[d.status]}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {d.status === 'pending' && (
                        <>
                          <button
                            onClick={() => quickStatus(d, 'accepted')}
                            className="p-1.5 rounded hover:bg-green-900/30 text-gray-400 hover:text-green-400 transition-colors"
                            title="Accept"
                          >
                            <CheckCircle className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => quickStatus(d, 'rejected')}
                            className="p-1.5 rounded hover:bg-red-900/30 text-gray-400 hover:text-red-400 transition-colors"
                            title="Reject"
                          >
                            <XCircle className="w-3.5 h-3.5" />
                          </button>
                        </>
                      )}
                      {d.status === 'accepted' && (
                        <button
                          onClick={() => quickStatus(d, 'active')}
                          className="px-2 py-1 rounded text-xs bg-green-900/20 border border-green-700 text-green-400 hover:bg-green-900/40 transition-colors"
                        >
                          Activate
                        </button>
                      )}
                      {d.status === 'active' && (
                        <button
                          onClick={() => quickStatus(d, 'completed')}
                          className="px-2 py-1 rounded text-xs bg-gray-800 border border-gray-700 text-gray-400 hover:text-white transition-colors"
                        >
                          Complete
                        </button>
                      )}
                      <button
                        onClick={() => openEdit(d)}
                        className="p-1.5 rounded hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
                        title="Edit"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="w-full max-w-lg rounded-2xl border border-gray-800 bg-[#0a0a0a] p-6 space-y-5 shadow-2xl">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">
                {editing ? 'Edit deal' : 'New sponsor deal'}
              </h2>
              <button
                onClick={() => setModalOpen(false)}
                className="text-gray-500 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3">
              {/* Sponsor */}
              <label className="block">
                <span className="text-xs text-gray-400 mb-1 block">Sponsor *</span>
                <select
                  value={form.sponsorId}
                  onChange={(e) => setForm((f) => ({ ...f, sponsorId: e.target.value }))}
                  className="w-full rounded-md border border-gray-700 bg-gray-900 px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#D72638]"
                >
                  <option value="">Select a sponsor…</option>
                  {sponsors.map((s) => (
                    <option key={s.sponsorId} value={s.sponsorId}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </label>

              {/* Film */}
              <label className="block">
                <span className="text-xs text-gray-400 mb-1 block">Film *</span>
                <select
                  value={form.filmId}
                  onChange={(e) => setForm((f) => ({ ...f, filmId: e.target.value }))}
                  className="w-full rounded-md border border-gray-700 bg-gray-900 px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#D72638]"
                >
                  <option value="">Select a film…</option>
                  {films.map((f) => (
                    <option key={f.filmId} value={f.filmId}>
                      {f.title ?? f.filmId}
                    </option>
                  ))}
                </select>
              </label>

              {/* Creator ID */}
              <label className="block">
                <span className="text-xs text-gray-400 mb-1 block">Creator user ID</span>
                <Input
                  value={form.creatorId}
                  onChange={(e) => setForm((f) => ({ ...f, creatorId: e.target.value }))}
                  placeholder="e.g. usr_abc123"
                  className="bg-gray-900 border-gray-700"
                />
              </label>

              {/* Amount */}
              <label className="block">
                <span className="text-xs text-gray-400 mb-1 block">Deal amount (USD) *</span>
                <Input
                  type="number"
                  min="0"
                  value={form.amount}
                  onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                  placeholder="e.g. 5000"
                  className="bg-gray-900 border-gray-700"
                />
              </label>

              {/* Ad creative */}
              <label className="block">
                <span className="text-xs text-gray-400 mb-1 block">Linked ad creative</span>
                <select
                  value={form.adId}
                  onChange={(e) => setForm((f) => ({ ...f, adId: e.target.value }))}
                  className="w-full rounded-md border border-gray-700 bg-gray-900 px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#D72638]"
                >
                  <option value="">None</option>
                  {ads.map((a) => (
                    <option key={a.adId} value={a.adId}>
                      {a.name ?? a.adId} ({a.type})
                    </option>
                  ))}
                </select>
              </label>

              {/* Terms */}
              <label className="block">
                <span className="text-xs text-gray-400 mb-1 block">Terms / notes</span>
                <textarea
                  value={form.terms}
                  onChange={(e) => setForm((f) => ({ ...f, terms: e.target.value }))}
                  rows={3}
                  placeholder="Deal terms, deliverables, or notes…"
                  className="w-full rounded-md border border-gray-700 bg-gray-900 px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#D72638] resize-none"
                />
              </label>

              {/* Status (edit only) */}
              {editing && (
                <label className="block">
                  <span className="text-xs text-gray-400 mb-1 block">Status</span>
                  <select
                    value={form.status}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, status: e.target.value as SponsorDealStatus }))
                    }
                    className="w-full rounded-md border border-gray-700 bg-gray-900 px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#D72638]"
                  >
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {STATUS_LABELS[s]}
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setModalOpen(false)}
                className="border-gray-700 text-gray-300 hover:text-white"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving || !form.sponsorId || !form.filmId || !form.amount}
                className="bg-[#D72638] hover:bg-[#D72638]/90 text-white"
              >
                {saving ? 'Saving…' : editing ? 'Save changes' : 'Create deal'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
